<template>
  <section class="statistics-card">
    <h3 class="card-title">
      {{ t('workbench.dataStats.serviceChannelStats') }}
    </h3>
    <div
      ref="chartRef"
      class="channel-chart"
    />
  </section>
</template>

<script setup>
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import { getServiceChannelStatistics } from '@/api/primaryChannelStatistics'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'

const { t, locale } = useI18n()

const CHANNEL_DEFS = [
  {
    code: ChannelIdentityTypeEnum.CITY_ASSOC.code,
    nameKey: 'workbench.dataStats.identityCityAssoc',
    color: '#6B4FF7'
  },
  {
    code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code,
    nameKey: 'workbench.dataStats.identityDistrictAssoc',
    color: '#1478F2'
  },
  {
    code: ChannelIdentityTypeEnum.ART_NEW_ORG.code,
    nameKey: 'workbench.dataStats.identityArtNewOrg',
    color: '#FFC51B'
  },
  {
    code: ChannelIdentityTypeEnum.SUPPORT_REGION.code,
    nameKey: 'workbench.dataStats.identitySupportRegion',
    color: '#16C68B'
  }
]

const channelDefinitions = computed(() => CHANNEL_DEFS.map(item => ({
  ...item,
  name: `${t(item.nameKey)}${t('workbench.dataStats.countSuffix')}`
})))

const chartRef = useTemplateRef('chartRef')
let chart = null
let resizeObserver = null
let resizeFrame = 0
let totalCount = 0
let channelItems = []

function buildDefaultItems() {
  return channelDefinitions.value.map(item => ({
    ...item,
    value: 0,
    percentage: '0.00%'
  }))
}

function buildOption() {
  const mobile = (chart?.getWidth() || 0) <= 680
  const center = mobile ? ['50%', '36%'] : ['32%', '50%']
  const items = channelItems.length ? channelItems : buildDefaultItems()
  const unitGe = t('workbench.dataStats.unitGe')
  return {
    color: items.map(item => item.color),
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params) => `${params.name}<br/>${params.value} ${unitGe}（${params.percent}%）`
    },
    legend: {
      orient: 'vertical',
      left: mobile ? 'center' : '53%',
      bottom: mobile ? 0 : 'center',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: mobile ? 12 : 26,
      icon: 'circle',
      textStyle: {
        color: '#333',
        fontSize: mobile ? 12 : 15,
        rich: {
          name: { width: mobile ? 220 : 285 },
          count: { width: 38, align: 'right', fontWeight: 'bold' },
          unit: { width: 24, color: '#555' },
          rate: { width: 50, align: 'right', color: '#555' }
        }
      },
      formatter: (name) => {
        const item = items.find(current => current.name === name)
        return `{name|${name}}{count|${item?.value || 0}}{unit| ${unitGe}}{rate|${item?.percentage || '0.00%'}}`
      }
    },
    graphic: [{
      type: 'group',
      left: center[0],
      top: center[1],
      bounding: 'raw',
      children: [
        {
          type: 'text',
          y: -12,
          style: {
            text: String(totalCount),
            fill: '#222',
            font: 'bold 34px "Microsoft YaHei"',
            textAlign: 'center',
            textVerticalAlign: 'middle'
          }
        },
        {
          type: 'text',
          y: 22,
          style: {
            text: t('workbench.dataStats.subChannelTotal'),
            fill: '#999',
            font: '14px "Microsoft YaHei"',
            textAlign: 'center',
            textVerticalAlign: 'middle'
          }
        }
      ]
    }],
    series: [{
      name: t('workbench.dataStats.serviceChannelStats'),
      type: 'pie',
      center,
      radius: mobile ? [72, 110] : [82, 128],
      startAngle: 90,
      clockwise: true,
      label: { show: false },
      labelLine: { show: false },
      data: items.map(item => ({
        name: item.name,
        value: item.value,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
            { offset: 0, color: item.color },
            { offset: 1, color: echarts.color.lift(item.color, 0.28) }
          ])
        }
      }))
    }]
  }
}

function renderChart() {
  chart?.setOption(buildOption(), { notMerge: true })
}

async function loadStatistics() {
  try {
    const response = await getServiceChannelStatistics()
    const payload = response && response.code === 200 ? (response.data || {}) : {}
    const sourceItems = Array.isArray(payload.items) ? payload.items : []
    totalCount = Number(payload.total) || 0
    channelItems = channelDefinitions.value.map((definition) => {
      const source = sourceItems.find(item => item.code === definition.code) || {}
      return {
        ...definition,
        name: definition.name,
        value: Number(source.value) || 0,
        percentage: source.percentage || '0.00%'
      }
    })
  } catch (error) {
    console.error('查询一级渠道服务渠道统计失败:', error)
    totalCount = 0
    channelItems = buildDefaultItems()
  }
  renderChart()
}

watch(locale, () => {
  loadStatistics()
})

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  channelItems = buildDefaultItems()
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadStatistics()
})

onBeforeUnmount(() => {
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 22px 24px 18px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.channel-chart {
  width: 100%;
  height: 360px;
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .channel-chart {
    height: 440px;
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .channel-chart {
    height: 420px;
  }
}
</style>
